import { useCallback, useState } from "react";
import {
	getServiceDropdownLabel,
	type CustomerInfo,
	type ServiceItem,
	type ServiceType,
} from "../utils/tickets";

export interface ServiceSelectionState {
	selectedCustomerId: number | null;
	selectedCustomerNameDisplay: string;
	selectedServiceIds: number[];
	selectedServiceTypes: ServiceType[];
	selectedSubscriptionIds: string[];
	selectedServiceLabels: string[];
}

export interface UseServiceSelectionReturn extends ServiceSelectionState {
	setSelectedCustomerId: (value: number | null) => void;
	setSelectedCustomerNameDisplay: (value: string) => void;
	setSelectedServiceIds: (value: number[]) => void;
	setSelectedServiceTypes: (value: ServiceType[]) => void;
	setSelectedSubscriptionIds: (value: string[]) => void;
	setSelectedServiceLabels: (value: string[]) => void;
	handleCustomerSelect: (
		customerNameDisplay: string,
		uniqueCustomers: CustomerInfo[]
	) => void;
	handleServiceSelect: (
		selectedLabels: string[],
		filteredServiceItems: ServiceItem[]
	) => void;
	reset: () => void;
}

/**
 * Customer + service(s) selection shared by the Kontrol Edge ticket modals.
 *
 * The dropdowns hand back labels; everything the payload needs — service ids,
 * types and above all `subscriptionId`, which reaches KS as the Service ID
 * custom field — is derived here, once, so the surfaces cannot disagree on
 * what a selected label means (CTX-533).
 */
export const useServiceSelection = (): UseServiceSelectionReturn => {
	const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
		null
	);
	const [selectedCustomerNameDisplay, setSelectedCustomerNameDisplay] =
		useState("");
	const [selectedServiceIds, setSelectedServiceIds] = useState<number[]>([]);
	const [selectedServiceTypes, setSelectedServiceTypes] = useState<
		ServiceType[]
	>([]);
	const [selectedSubscriptionIds, setSelectedSubscriptionIds] = useState<
		string[]
	>([]);
	const [selectedServiceLabels, setSelectedServiceLabels] = useState<string[]>(
		[]
	);

	const clearServices = useCallback(() => {
		setSelectedServiceIds([]);
		setSelectedServiceTypes([]);
		setSelectedSubscriptionIds([]);
		setSelectedServiceLabels([]);
	}, []);

	const handleCustomerSelect = useCallback(
		(customerNameDisplay: string, uniqueCustomers: CustomerInfo[]) => {
			const customer = uniqueCustomers.find(
				(c) => c.nameDisplay === customerNameDisplay
			);
			if (!customer) return;

			setSelectedCustomerId(customer.id);
			setSelectedCustomerNameDisplay(customer.nameDisplay);
			// Services belong to the previous customer and are not valid for the
			// new one, so they go rather than travelling into the payload.
			clearServices();
		},
		[clearServices]
	);

	const handleServiceSelect = useCallback(
		(selectedLabels: string[], filteredServiceItems: ServiceItem[]) => {
			setSelectedServiceLabels(selectedLabels);

			const selectedServices = filteredServiceItems.filter((item) =>
				selectedLabels.includes(getServiceDropdownLabel(item))
			);

			setSelectedServiceIds(selectedServices.map((s) => s.id));
			setSelectedServiceTypes(selectedServices.map((s) => s.type));
			setSelectedSubscriptionIds(
				selectedServices.map((s) => s.subscriptionId ?? "").filter(Boolean)
			);
		},
		[]
	);

	const reset = useCallback(() => {
		setSelectedCustomerId(null);
		setSelectedCustomerNameDisplay("");
		clearServices();
	}, [clearServices]);

	return {
		selectedCustomerId,
		selectedCustomerNameDisplay,
		selectedServiceIds,
		selectedServiceTypes,
		selectedSubscriptionIds,
		selectedServiceLabels,
		setSelectedCustomerId,
		setSelectedCustomerNameDisplay,
		setSelectedServiceIds,
		setSelectedServiceTypes,
		setSelectedSubscriptionIds,
		setSelectedServiceLabels,
		handleCustomerSelect,
		handleServiceSelect,
		reset,
	};
};
